import { GetServerSidePropsContext, NextPage } from 'next'
import QueryString from 'qs'

import {
  GetServerSidePropsReturnType,
  PageProps,
} from '../../relay/Common/PageProps'
import getConfiguration from '../../rest/services/getConfiguration'
import RestApiClient from '../../rest/client'
import { initWithLangAndLabels } from '../../i18n'
import ProductsTemplate from '../../templates/ProductsTemplate'
import brandBySlug from '../../rest/requests/brands/brandBySlug'
import { Brand } from '../../rest/types/Brand'
import getOffersWithTotal from '../../rest/requests/offers/getOffersWithTotal'
import { Offers } from '../../rest/types/Offer'
import useOfferList from '../../relay/Offer/useOfferList'
import { ProductsFiltersFormValues } from '../../components/ProductsFilters'
import { OFFERS_LIST_LIMIT } from '../../constant'
import { SeoTransformer } from '../../relay/Transformers/SeoTransformer'
import Router from '../../routes/Router'
import routes from '../../routes/definitions'

type Props = PageProps & {
  brand: Brand
  offers: Offers
  total: number
  filters?: ProductsFiltersFormValues
}

const BrandPage: NextPage<Props> = ({
  brand,
  offers,
  total,
  configuration,
  filters,
}) => {
  const props = useOfferList({
    brand,
    offers,
    total,
    configuration,
    filters,
  })

  return <ProductsTemplate {...props} />
}

export default BrandPage

export async function getServerSideProps(
  context: GetServerSidePropsContext
): GetServerSidePropsReturnType<Props> {
  const slug: string = String(context?.query?.slug ?? '')
  const filtersParams: any = context?.query?.filters
    ? QueryString.parse(context?.query?.filters as string, {
        delimiter: ',',
      })
    : null

  const limit = context?.query?.limit ? (context?.query?.limit as string) : null

  const [configuration, rs] = await Promise.all([
    getConfiguration(),
    RestApiClient.call(brandBySlug({ slug })),
  ])

  const brand = rs?.data
  const i18n = initWithLangAndLabels('fr', configuration.translations)

  const [offersResult] = await Promise.all([
    RestApiClient.call(
      getOffersWithTotal({
        filters: {
          brand: {
            documentId: brand?.id,
          },
          ...(filtersParams && {
            ...filtersParams?.filters,
          }),
        },
        limit: limit || OFFERS_LIST_LIMIT,
      })
    ),
  ])

  const offers = offersResult?.data?.offers ?? []
  const total = offersResult?.data?.total ?? 0

  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59'
  )

  if (!brand) {
    return {
      notFound: true,
      props: {
        i18n,
        configuration,
      },
    }
  }

  return {
    props: {
      brand,
      metas: SeoTransformer(
        Router.getRouteUrl(routes.brand, context?.params),
        brand?.seo
      ),
      offers,
      total,
      configuration,
      filters: filtersParams?.filters || null,
      i18n,
    },
  }
}
